import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CommunityMessage,
  CommunityMessageListResponse,
  CommunityMessagePayload,
  CommunityMessageView,
  CommunityRecipientsResponse
} from '../models/community-messages.models';

/**
 * Private messages (`/api/messages`). The backend applies the community's
 * communication rules: `recipients` lists whom the viewer may write to and
 * `send` answers 422 with `errors.recipient` for anyone else.
 */
@Injectable({ providedIn: 'root' })
export class CommunityMessagesService {
  private readonly base = '/messages';

  constructor(private http: HttpClient) {}

  list(view: CommunityMessageView, page = 1, perPage = 20): Observable<CommunityMessageListResponse> {
    const params = new HttpParams()
      .set('view', view)
      .set('page', String(page))
      .set('per_page', String(perPage));
    return this.http.get<CommunityMessageListResponse>(this.base, { params });
  }

  /** Marks the message read when the viewer is its recipient. */
  get(id: number): Observable<{ message: CommunityMessage }> {
    return this.http.get<{ message: CommunityMessage }>(`${this.base}/${id}`);
  }

  send(message: CommunityMessagePayload): Observable<{ message: CommunityMessage }> {
    return this.http.post<{ message: CommunityMessage }>(this.base, { message });
  }

  /**
   * The reply goes to the other party of `parentId`; subject and recipient are
   * derived by the backend. `content` is read from the top level, not from a
   * `message` wrapper (see MessagesController#reply).
   */
  reply(parentId: number, content: string): Observable<{ message: CommunityMessage }> {
    return this.http.post<{ message: CommunityMessage }>(`${this.base}/${parentId}/reply`, { content });
  }

  archive(id: number, archive: boolean): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.base}/${id}/${archive ? 'archive' : 'unarchive'}`, {});
  }

  /** `search` is only honoured for admins (id, e-mail, name or username). */
  recipients(search?: string | null): Observable<CommunityRecipientsResponse> {
    let params = new HttpParams();
    if (search?.trim()) params = params.set('search', search.trim());
    return this.http.get<CommunityRecipientsResponse>(`${this.base}/recipients`, { params });
  }

  unreadCount(): Observable<{ unread_count: number }> {
    return this.http.get<{ unread_count: number }>(`${this.base}/unread_count`);
  }

  /** True when a 422 came back because the recipient is not reachable under the communication rules. */
  static isRecipientUnreachable(error: unknown): boolean {
    const err = error as { status?: number; error?: { errors?: Record<string, unknown> } } | null;
    return err?.status === 422 && !!err.error?.errors && 'recipient' in err.error.errors;
  }
}
